import ApplicationData from '../types/ApplicationData'
import ConfigurationData from '../types/Configuration'
import ImageData from '../types/ImageData'
import ModuleData from '../types/ModuleData'

const hasImage = <Key extends string>(
  item: Partial<ModuleData> | undefined,
  key: Key
): item is Partial<ModuleData> &
  {
    [K in Key]: ImageData
  } => {
  return !!item && key in item && !!item[key as keyof typeof item]
}

const prefetchImage = (url: string): Promise<boolean> =>
  new Promise((resolve) => {
    const img = new Image()
    img.onload = () => resolve(true)
    img.onerror = () => resolve(false)
    img.src = url
  })

const moduleImages = (module: Partial<ModuleData>): string[] => {
  const result: string[] = []
  for (const key of ['preview', 'thumbnail', 'thumbnailHover', 'heroImage'] as const) {
    if (hasImage(module, key)) {
      result.push(module[key].url)
    }
  }
  for (const variation of module.variations ?? []) {
    for (const key of ['preview', 'thumbnail', 'thumbnailHover', 'heroImage'] as const) {
      if (hasImage(variation.override, key)) {
        result.push(variation.override[key].url)
      }
    }
  }
  return result
}

const applicationImages = (application: ApplicationData): string[] => {
  let result: string[] = []
  result.push(application.backgroundImage.url)
  for (const luminary of application.luminaries) {
    result.push(luminary.overlayImage?.url ?? '')
  }
  for (const module of application.availableModules) {
    result = result.concat(moduleImages(module))
  }
  return result
}

const allImages = (configuration: ConfigurationData): string[] => {
  let images: string[] = []
  for (const module of configuration.modules) {
    images = images.concat(moduleImages(module))
  }
  for (const application of configuration.applications) {
    images = images.concat(applicationImages(application))
  }
  for (const asset of Object.values(configuration.assets)) {
    if (asset?.url) {
      images.push(asset.url)
    }
  }
  images = images.filter((x) => !!x).filter((x, i) => images.indexOf(x) === i)
  return images
}

export const loadConfiguration = async (url?: string): Promise<ConfigurationData> => {
  if (!url) {
    throw new Error('No configuration URL given')
  }
  const response = (await jQuery.ajax(url).promise()).result as ConfigurationData
  await Promise.all(allImages(response).map(prefetchImage))
  return response
}
